'use client'

import { ButtonLink, Faq, html } from '@ensemble.music/ui'
import { ArrowDown, ArrowRight, Check } from 'lucide-react'
import { MotionConfig, motion } from 'motion/react'
import { Fragment } from 'react'
import { Benefits } from '../landingpage/Benefits'
import { CONTACT_EMAIL } from '../landingpage/content'
import { FooterLandingPage } from '../landingpage/Footer'
import { HowItWorks } from '../landingpage/HowItWorks'
import { Parallax } from '../landingpage/Parallax'
import { SectionHeading } from '../landingpage/SectionHeading'
import { SiteHeader } from '../wizard/WizardShell'
import { bandBenefits, bandFaq, bandHero, bandSteps, exampleRequest, SIGNUP_HREF } from './content'

const rise = (index: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, delay: 0.08 * index, ease: 'easeOut' as const },
})

// Shorter than /buchen on purpose (owner request 2026-10-18): one promise - more gigs, more
// revenue - and every block ends in the same signup link. Steps and claims reuse the /buchen
// sections, so both pages share one look (owner request 2026-10-19).
export function FuerBandsScreen() {
  return (
    <MotionConfig reducedMotion="user">
      <html.div className="flex flex-col min-h-screen bg-background">
        <SiteHeader
          href="/fuer-bands"
          action={
            <ButtonLink
              href={SIGNUP_HREF}
              className="whitespace-nowrap"
            >
              {bandHero.button}
            </ButtonLink>
          }
        />

        <html.main
          id="inhalt"
          className="flex flex-col grow"
        >
          <html.section className="flex flex-row flex-wrap items-center gap-16 w-full max-w-[1120px] mx-auto px-8 pt-16 pb-32 max-sm:px-4 max-sm:pt-10 max-sm:pb-20 max-sm:gap-10">
            <html.div className="flex flex-col gap-6 grow shrink basis-[420px]">
              <html.p className="text-sm font-semibold uppercase tracking-wide text-primary-text">
                {bandHero.overline}
              </html.p>
              {/* not animated: the H1 is the LCP element (same rule as the booker hero) */}
              <html.h1 className="text-5xl font-semibold leading-tight tracking-tight text-color max-md:text-4xl max-sm:text-3xl">
                {/* plain spans: whitespace-nowrap is not resolved by the Tailwind frontend here */}
                {bandHero.headline.map((sentence, index) => (
                  <Fragment key={sentence}>
                    {index > 0 ? ' ' : null}
                    <span className="landing-nowrap">{sentence}</span>
                  </Fragment>
                ))}
              </html.h1>
              <motion.div {...rise(1)}>
                <html.p className="text-lg text-color-secondary max-w-[560px]">
                  {bandHero.subline}
                </html.p>
              </motion.div>
              <motion.div {...rise(2)}>
                <html.div className="flex flex-row flex-wrap items-center gap-3">
                  <ButtonLink
                    href={SIGNUP_HREF}
                    size="lg"
                  >
                    {bandHero.button}
                  </ButtonLink>
                  <ButtonLink
                    href="#so-funktionierts"
                    tone="ghost"
                    size="lg"
                  >
                    {bandHero.secondary}
                    <ArrowDown
                      size={20}
                      aria-hidden="true"
                    />
                  </ButtonLink>
                </html.div>
              </motion.div>
              <motion.div {...rise(3)}>
                <html.ul className="flex flex-row flex-wrap gap-x-6 gap-y-2">
                  {bandHero.checks.map((check) => (
                    <html.li
                      key={check}
                      className="flex flex-row items-center gap-2"
                    >
                      <html.span className="text-success">
                        <Check
                          size={18}
                          aria-hidden="true"
                        />
                      </html.span>
                      <html.span className="text-base text-color-secondary">{check}</html.span>
                    </html.li>
                  ))}
                </html.ul>
              </motion.div>
            </html.div>

            {/* The photo carries the hero; the example request sits on its corner, so the page
                shows what a band receives without another stand-alone card. Custom classes stay
                on plain DOM elements (see HowItWorks). */}
            <motion.div
              {...rise(4)}
              className="landing-band-visual"
            >
              <Parallax distance={24}>
                <div className="landing-band-photo">
                  <img
                    src={bandHero.image}
                    alt="Jazz-Combo spielt live auf einer Bühne"
                    className="landing-band-photo-image"
                  />
                  <span className="landing-photo-credit">@unsplash</span>
                </div>
              </Parallax>
              <div className="landing-band-example">
                <html.div className="flex flex-col gap-3 bg-surface border border-border-color rounded-card p-5 shadow-lg">
                  <html.div className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <html.p className="text-sm font-semibold text-color-tertiary">
                      <span className="landing-nowrap">{exampleRequest.label}</span>
                    </html.p>
                    <html.span className="rounded-full px-3 py-1 bg-primary-soft text-primary-text text-sm font-semibold">
                      <span className="landing-nowrap">Passt zu euch</span>
                    </html.span>
                  </html.div>
                  {exampleRequest.rows.map(([term, value]) => (
                    <html.div
                      key={term}
                      className="flex flex-row justify-between gap-4 border-t border-border-color-subtle pt-2"
                    >
                      <html.p className="text-sm text-color-secondary">{term}</html.p>
                      <html.p className="text-sm font-semibold text-color">{value}</html.p>
                    </html.div>
                  ))}
                </html.div>
              </div>
            </motion.div>
          </html.section>

          <html.div className="flex flex-col gap-40 w-full max-w-[1120px] mx-auto px-8 pb-40 max-sm:px-4 max-sm:gap-24 max-sm:pb-24">
            <HowItWorks
              steps={bandSteps}
              title="So kommt ihr zu Auftritten"
              lead="Drei Schritte, kein Account."
            />

            <Benefits
              items={bandBenefits}
              title="Was ihr davon habt"
              lead="Wir kümmern uns um die Anfragen, ihr um die Musik."
            />

            <section className="landing-faq">
              <div className="landing-faq-head">
                <SectionHeading
                  size="sm"
                  title="Häufige Fragen"
                />
              </div>
              <html.div className="flex flex-col grow min-w-0">
                <Faq items={bandFaq} />
              </html.div>
            </section>
          </html.div>

          <html.section className="flex flex-row justify-center bg-primary-soft">
            <html.div className="flex flex-col items-center gap-5 w-full max-w-[1120px] mx-auto px-8 py-20 text-center max-sm:px-4 max-sm:py-14">
              <html.h2 className="max-w-[720px] text-4xl font-semibold leading-tight tracking-tight text-color max-md:text-3xl max-sm:text-2xl">
                Euer nächster Auftritt fängt hier an
              </html.h2>
              <html.p className="max-w-[560px] text-lg text-color-secondary">
                {bandHero.checks.join(' · ')}
              </html.p>
              <ButtonLink
                href={SIGNUP_HREF}
                size="lg"
              >
                {bandHero.button}
                <ArrowRight
                  size={20}
                  aria-hidden="true"
                />
              </ButtonLink>
              <html.p className="text-base text-color-secondary">
                Lieber erst eine Frage? Schreibt uns:{' '}
                <html.a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-link hover:underline"
                >
                  {CONTACT_EMAIL}
                </html.a>
              </html.p>
            </html.div>
          </html.section>
        </html.main>

        <FooterLandingPage />
      </html.div>
    </MotionConfig>
  )
}
